import { CallAPI } from "../../apiManager/ApiCall";
import {
  translatePinURL,
  translatePinsForSlideURL,
  translateAnatomyAnnotationURL,
  translateAnatomyAnnotationsForSlideURL,
} from "../../apiManager/APIConfig";

/**
 * Translate the title + body of ONE slide pin into every supported
 * app locale. **UPSERT** — re-running OVERWRITES any existing
 * per-locale value for the pin. Source is auto-picked: EN if present,
 * otherwise the first existing locale.
 *
 * Resolves to:
 *   { status, message, appLangs,
 *     pinId, sourceLang, targetLangs,
 *     results: [{lang, action: "created"|"updated"}],
 *     created, updated }
 */
export const translatePinCall = async (pinId) => {
  if (!pinId) return { status: 400, message: "Missing pin id." };
  return CallAPI({
    url: translatePinURL,
    method: "POST",
    body: { pin_id: pinId },
  });
};

/**
 * Translate EVERY pin on a histology / embryology slide into every
 * supported app locale. **UPSERT** — re-running overwrites any existing
 * per-locale values for each pin.
 */
export const translatePinsForSlideCall = async (slideId) => {
  if (!slideId) return { status: 400, message: "Missing slide id." };
  return CallAPI({
    url: translatePinsForSlideURL,
    method: "POST",
    body: { slide_id: slideId },
  });
};

/** Resolve a subtitle card label from its localization rows. */
export function subtitleDisplayTitle(subtitle) {
  return (
    subtitle?.titleLocalizations?.find((e) => e?.field === "title")?.value ||
    "(untitled)"
  );
}

/** Collect unique slide ids under a parent title (all subtitles). */
export function collectSlidesFromTitle(titleItem) {
  const entries = [];
  for (const subtitle of titleItem?.subtitles || []) {
    const subtitleTitle = subtitleDisplayTitle(subtitle);
    const slides = [
      ...(subtitle?.slides || []),
      ...(subtitle?.referencedSlides || []),
    ];
    const seen = new Set();
    for (const slide of slides) {
      if (!slide?.id || seen.has(slide.id)) continue;
      seen.add(slide.id);
      entries.push({
        slideId: slide.id,
        subtitleTitle,
        subtitleId: subtitle.id,
      });
    }
  }
  return entries;
}

/** Collect unique slide ids for one subtitle card. */
export function collectSlidesFromSubtitle(subtitle) {
  const subtitleTitle = subtitleDisplayTitle(subtitle);
  const slides = [
    ...(subtitle?.slides || []),
    ...(subtitle?.referencedSlides || []),
  ];
  const seen = new Set();
  const entries = [];
  for (const slide of slides) {
    if (!slide?.id || seen.has(slide.id)) continue;
    seen.add(slide.id);
    entries.push({
      slideId: slide.id,
      subtitleTitle,
      subtitleId: subtitle.id,
    });
  }
  return entries;
}

/**
 * Translate every pin on each slide entry via translate-slide.
 * Calls setStatus(subtitleTitle, slideIndex) for progress UI.
 */
export async function translatePinsForSlideEntries(entries, setStatus) {
  if (!entries?.length) {
    return { status: 422, message: "No slides found to translate pins on." };
  }

  const totals = {
    slideCount: entries.length,
    slidesProcessed: 0,
    subtitleCount: new Set(entries.map((e) => e.subtitleId)).size,
    pinCount: 0,
    pinsTranslated: 0,
    pinsFailed: 0,
    pinsSkipped: 0,
    created: 0,
    updated: 0,
    slideResults: [],
  };

  for (let i = 0; i < entries.length; i++) {
    const { slideId, subtitleTitle } = entries[i];
    setStatus?.(
      `Translating pins — "${subtitleTitle}" (slide ${i + 1} of ${entries.length})…`,
    );
    const res = await translatePinsForSlideCall(slideId);
    if (typeof res?.status === "number" && res.status >= 400) {
      return res;
    }
    totals.slidesProcessed += 1;
    totals.pinCount += res?.pinCount ?? 0;
    totals.pinsTranslated += res?.pinsTranslated ?? 0;
    totals.pinsFailed += res?.pinsFailed ?? 0;
    totals.pinsSkipped += res?.pinsSkipped ?? 0;
    totals.created += res?.created ?? 0;
    totals.updated += res?.updated ?? 0;
    totals.slideResults.push({
      slideId,
      subtitleTitle,
      pinCount: res?.pinCount ?? 0,
      pinsTranslated: res?.pinsTranslated ?? 0,
      pinsFailed: res?.pinsFailed ?? 0,
      pinsSkipped: res?.pinsSkipped ?? 0,
    });
  }

  return {
    status: 200,
    message: `Translated ${totals.pinsTranslated} of ${totals.pinCount} pin(s) across ${totals.slidesProcessed} slide(s).`,
    ...totals,
  };
}

/**
 * Translate the name + description of ONE 3D-model annotation into
 * every supported app locale. **UPSERT** — re-running overwrites
 * any existing per-locale row.
 */
export const translateAnatomyAnnotationCall = async (
  annotationId,
  { sourceLang } = {},
) => {
  if (!annotationId)
    return { status: 400, message: "Missing annotation id." };
  return CallAPI({
    url: translateAnatomyAnnotationURL,
    method: "POST",
    body: {
      annotation_id: annotationId,
      ...(sourceLang ? { source_lang: sourceLang } : {}),
    },
  });
};

/**
 * Translate EVERY annotation on a given 3D slide. Convenience for
 * "I just finished labelling this model, ship it in all languages."
 */
export const translateAnatomyAnnotationsForSlideCall = async (
  slideId,
  { sourceLang } = {},
) => {
  if (!slideId) return { status: 400, message: "Missing slide id." };
  return CallAPI({
    url: translateAnatomyAnnotationsForSlideURL,
    method: "POST",
    body: {
      slide_id: slideId,
      ...(sourceLang ? { source_lang: sourceLang } : {}),
    },
  });
};
